import React, { useCallback, useMemo } from 'react'
import { css } from '@linaria/core'
import { ArrowCaret, PlaceWorld } from '@fs/zion-icon'
import { colors, fonts, LabelTooltip } from '@fs/zion-ui'
import { getCountryCallingCode } from 'react-phone-number-input'
import type { InternationalAlpha2 } from './locations'
import { getAlpha3From2 } from './locations'
import { INTERNATIONAL_CODE, INTERNATIONAL_VALUE } from './constants'
import type { PhoneAlpha2, PhoneAlpha3 } from './types'

const selectedItemContainerCss = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  pointer-events: none;
  padding: 0 4px;
  height: 100%;
  width: 100%;
  border-inline-end: 1px solid ${colors.gray05};
  color: ${colors.gray100};
`

const containerCss = css`
  position: relative;
  /* must match input height */
  height: 36px;
  border-start-start-radius: 4px;
  border-end-start-radius: 4px;
  background: ${colors.gray02};
  /* add pseudo selectors for focus and hover to the container since select has opacity: 0 */
  /* focus-within so that it works when select-descendant is focused */
  &:focus-within {
    background: ${colors.transparent.color03};
  }
  &:hover {
    background: ${colors.transparent.color03};
  }
`

const selectCss = css`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  height: 100%;
  width: 100%;

  // use similar styling to zion-ui Select
  appearance: none; // remove native select arrow
  font-family: ${fonts.body};
  letter-spacing: 0.005em;
  background: ${colors.gray00};
  color: ${colors.transparent.gray100};
  cursor: pointer;
  font-size: 1rem; // for now matching the current zion-ui Select font-size

  // hide select so custom aria-hidden selectedItem element is visible but select still handles interaction
  opacity: 0;
`

const selectedItemCss = css`
  font-size: 14px;
  padding-bottom: 1px;
  /* shouldn't need to wrap but can be an issue on small phones wanting to use 2 lines */
  white-space: nowrap;
`

// Likely the BaseOption will always use undefined instead of InternationalAlpha2 but both cases should be handled just in case
// in the code
type BaseOption = { value: InternationalAlpha2 | PhoneAlpha2; divider?: boolean }

type Option = BaseOption & {
  callingCode: string
  alpha3: PhoneAlpha3
  fullLabel: string
  shortLabel: string
}

type LocationSelectProps = {
  options: BaseOption[]
  'aria-label': string
  value: string
  onChange: (selectedItem: PhoneAlpha2 | typeof INTERNATIONAL_VALUE) => void
}

export default function LocationSelect({
  // passed from react-phone-number-input--each option has value and label (or divider: true)
  options: baseOptions = [],
  'aria-label': ariaLabel,
  // value will be undefined for international
  value: unsafeSelectedItem,
  onChange,
}: LocationSelectProps): JSX.Element {
  // fallback to defaultLocation here just in case react-phone-number-input misbehaves
  // (not likely but possible during initialization or edge case)
  const selectedItem = unsafeSelectedItem || INTERNATIONAL_CODE
  const options = useOptions(baseOptions)
  const selectedOption = useSelectedOption(selectedItem, options)
  const handleOnChange = useCallback(
    (event) => {
      const value = event.target.value
      onChange(value === INTERNATIONAL_CODE ? INTERNATIONAL_VALUE : value)
    },
    [onChange]
  )
  return (
    <div className={containerCss}>
      <span className={selectedItemContainerCss} aria-hidden>
        {selectedOption.value === INTERNATIONAL_CODE ? (
          <PlaceWorld />
        ) : (
          <span className={selectedItemCss}>{selectedOption.shortLabel}</span>
        )}
        &nbsp;
        <ArrowCaret size="xs" direction="down" />
      </span>
      <LabelTooltip
        target={
          <select value={selectedItem} className={selectCss} onChange={handleOnChange} aria-label={ariaLabel}>
            {options.map((location) => {
              const { value, divider, fullLabel } = location
              if (divider) {
                return (
                  <option key="divider" disabled>
                    ─────────────
                  </option>
                )
              }
              return (
                <option key={value} value={value}>
                  {fullLabel}
                </option>
              )
            })}
          </select>
        }
        label={selectedOption.fullLabel}
        placement="end"
      />
    </div>
  )
}

function useSelectedOption(selectedItem, options): Option {
  return useMemo(() => {
    return options.find(({ value }) => value === selectedItem) ?? {}
  }, [options, selectedItem])
}

function useOptions(baseOptions): Option[] {
  return useMemo(() => {
    return baseOptions.map((option) => {
      if (option.divider) {
        return option
      }
      if (!option.value || option.value === INTERNATIONAL_CODE) {
        // set value as the code here because a value should be a string
        return { ...option, value: INTERNATIONAL_CODE, fullLabel: option.label, shortLabel: option.label }
      }
      const callingCode = getCountryCallingCode(option.value)
      const alpha3 = getAlpha3From2(option.value)
      const fullLabel = `${option.label} +${callingCode}`
      const shortLabel = `${alpha3} +${callingCode}`
      return {
        ...option,
        callingCode,
        alpha3,
        fullLabel,
        shortLabel,
      }
    })
  }, [baseOptions])
}
